import React, { useRef } from 'react';
import type { AnnotationTool } from '../types';
import { ColorPicker } from './ColorPicker';
import { ToolDropdown } from './ToolDropdown';
import { ToolbarOverflowMenu } from './ToolbarOverflowMenu';
import { useResponsiveToolbar } from '../hooks/useResponsiveToolbar';

interface ToolbarProps {
  activeTool: AnnotationTool;
  onToolChange: (tool: AnnotationTool) => void;
  onUndo: () => void;
  onRedo: () => void;
  canUndo: boolean;
  canRedo: boolean;
  onDiscard: () => void;
  onSave?: () => void;
  isEditing?: boolean;
  activeColor: string;
  onColorChange: (color: string) => void;
}

export const Toolbar = React.memo(function Toolbar({
  activeTool,
  onToolChange,
  onUndo,
  onRedo,
  canUndo,
  canRedo,
  onDiscard,
  onSave,
  isEditing,
  activeColor,
  onColorChange,
}: ToolbarProps) {
  const toolbarRef = useRef<HTMLDivElement>(null);
  const overflowed = useResponsiveToolbar(toolbarRef);

  // Clear the slide-in animation after it finishes so its persisted
  // max-height doesn't clip the tool dropdown menu.
  const handleAnimationEnd = React.useCallback((e: React.AnimationEvent<HTMLDivElement>) => {
    if (e.animationName === 'kollab-toolbar-slide-in') {
      (e.currentTarget as HTMLDivElement).style.animation = 'none';
    }
  }, []);

  const saveLabel = isEditing ? 'Save' : 'Submit Comment';
  return (
    <div
      ref={toolbarRef}
      className="kollab-annotation-toolbar"
      role="toolbar"
      aria-label="Annotation tools"
      onAnimationEnd={handleAnimationEnd}
    >
      <div className="kollab-annotation-toolbar-left">
        {!overflowed.has('tool') && (
          <ToolDropdown activeTool={activeTool} onToolChange={onToolChange} />
        )}

        {!overflowed.has('color') && (
          <ColorPicker activeColor={activeColor} onColorChange={onColorChange} />
        )}

        {!overflowed.has('undo') && (
          <button
            className="kollab-annotation-toolbar-btn"
            onClick={onUndo}
            disabled={!canUndo}
            aria-label="Undo"
            data-tooltip="Undo"
            type="button"
          >
            <div className="fa-icon-undo-regular"></div>
          </button>
        )}
        {!overflowed.has('redo') && (
          <button
            className="kollab-annotation-toolbar-btn"
            onClick={onRedo}
            disabled={!canRedo}
            aria-label="Redo"
            data-tooltip="Redo"
            type="button"
          >
            <div className="fa-icon-redo-regular"></div>
          </button>
        )}

        <ToolbarOverflowMenu
          overflowed={overflowed}
          activeTool={activeTool}
          onToolChange={onToolChange}
          activeColor={activeColor}
          onColorChange={onColorChange}
          onUndo={onUndo}
          onRedo={onRedo}
          canUndo={canUndo}
          canRedo={canRedo}
        />
      </div>

      <div className="kollab-annotation-toolbar-right">
        <button
          className="kollab-annotation-toolbar-btn kollab-annotation-discard-btn"
          onClick={onDiscard}
          aria-label="Discard"
          data-tooltip="Discard"
          type="button"
        >
          Discard
        </button>
        {onSave && (
          <button
            className="kollab-annotation-toolbar-btn kollab-annotation-save-btn actionbtn"
            onClick={onSave}
            aria-label={saveLabel}
            data-tooltip={saveLabel}
            type="button"
          >
            {saveLabel}
          </button>
        )}
      </div>
    </div>
  );
});
